import { executionForResponse } from '@/analytics/requests';
import {
  beginSearch, contextForPageRequest, contextForDeferredCount, isCurrentSearch,
  navigationMethod, resultCount, resultsPageForResponse, resultsPageFromCache,
} from '@/analytics/features/search-ui';
import {
  filterSnapshot, invalidFilterRanges, recordFilterApplyBlocked, recordFiltersApplied,
  rememberFilterOptions, moduleFilterValues,
} from '@/analytics/features/filters';
import { newId } from '@/analytics/client';
import { beginAction, capturePageExecution, currentAccountScope, sameAccount } from '@/analytics/context';
import {
  buildActiveModuleFilters,
  createEmptyCompositeGroups,
  hasInvalidCompositeGroups,
  isCompositeGroupConfigured,
  MAX_COMPOSITE_GROUPS_PER_TYPE,
} from '@/modules/core/compositeQuery';
import { normalizePortfolioInterestRequestForApi } from '@/utils/portfolioInterestModes';
import { message } from 'antd';
import { fetchPortfolioScopeInterests } from '../../Profile/service';
import { applyCategoryDeepLinkState } from './categoryDeepLinkState';
import {
  addProductsToList,
  autoCompleteIngredientName,
  createProductList,
  fetchFilterOptions,
  fetchProduct,
  fetchProductCategories,
  fetchProductLists,
  fetchProductsByIngredient,
  fetchProductsByIngredientCount,
} from './service';

const PRODUCT_COUNT_POLL_INTERVAL_MS = 1000;
const PRODUCT_COUNT_MAX_POLLS = 60;
const waitForProductCount = (milliseconds: number) =>
  new Promise((resolve) => {
    setTimeout(resolve, milliseconds);
  });

const DEFAULT_INTEREST_TOGGLES = {
  marketers: false,
  manufacturers: false,
  brands: false,
  ingredients: false,
  flavours: false,
  fragrances: false,
  packaging_types: false,
  packaging_materials: false,
};

const INTEREST_KEYS = [
  'marketers',
  'manufacturers',
  'brands',
  'ingredients',
  'flavours',
  'fragrances',
  'packaging_types',
  'packaging_materials',
];
const DEFAULT_INTEREST_ACTIONS = {
  marketers: 'include',
  manufacturers: 'include',
  brands: 'include',
  ingredients: 'include',
  flavours: 'include',
  fragrances: 'include',
  packaging_types: 'include',
  packaging_materials: 'include',
};

const normalizeInterestToggles = (interestToggles: any = {}) => ({
  marketers: interestToggles.marketers === true,
  manufacturers: interestToggles.manufacturers === true,
  brands: interestToggles.brands === true,
  ingredients: interestToggles.ingredients === true,
  flavours: interestToggles.flavours === true,
  fragrances: interestToggles.fragrances === true,
  packaging_types: interestToggles.packaging_types === true,
  packaging_materials: interestToggles.packaging_materials === true,
});

const normalizeInterestActions = (interestActions: any = {}) => ({
  marketers: interestActions.marketers === 'exclude' ? 'exclude' : 'include',
  manufacturers: interestActions.manufacturers === 'exclude' ? 'exclude' : 'include',
  brands: interestActions.brands === 'exclude' ? 'exclude' : 'include',
  ingredients: interestActions.ingredients === 'exclude' ? 'exclude' : 'include',
  flavours: interestActions.flavours === 'exclude' ? 'exclude' : 'include',
  fragrances: interestActions.fragrances === 'exclude' ? 'exclude' : 'include',
  packaging_types: interestActions.packaging_types === 'exclude' ? 'exclude' : 'include',
  packaging_materials: interestActions.packaging_materials === 'exclude' ? 'exclude' : 'include',
});

const normalizeInterestSelections = (interestSelections: any = {}) =>
  INTEREST_KEYS.reduce((acc: any, key) => {
    if (Array.isArray(interestSelections[key])) {
      acc[key] = [...new Set(interestSelections[key].map(Number))].filter(
        (id) => Number.isInteger(id) && id > 0,
      );
    }
    return acc;
  }, {});

const hasPortfolioScopeInterests = (scope: any = {}) =>
  (Array.isArray(scope.applications) && scope.applications.length > 0) ||
  (Array.isArray(scope.countries_of_origin) && scope.countries_of_origin.length > 0) ||
  (Array.isArray(scope.countries_of_sale) && scope.countries_of_sale.length > 0);

const Model = {
  namespace: 'search',
  state: {
    ingredientSearchInput: '',
    appliedSearch: undefined,
    pageRequest: undefined,
    resultsPage: undefined,
    searchRequestToken: undefined,
    filterMetadata: {},
    selectedIngredients: [], // [{id: number, name: string}]
    filters: {},
    filterOptions: {},
    productsData: {
      pages: {}, // { pageNumber: { data: [...], total: number } }
      total: null,
      countKey: null,
      countStatus: 'idle',
    },
    currentPage: 1,
    pageSize: 10,
    productLists: [],
    selectedRows: [],
    ingredientSuggestions: [],
    filterDrawerVisible: false,
    listModalVisible: false,
    productCategories: [],
    productCategoriesLoaded: false,
    filterOptionsLoaded: false,
    interestToggles: { ...DEFAULT_INTEREST_TOGGLES },
    interestActions: { ...DEFAULT_INTEREST_ACTIONS },
    interestSelections: {},
    moduleFilters: {},
    queryMode: 'simple',
    compositeGroups: createEmptyCompositeGroups(),
    compositeGroupSequence: 0,
    viewMode: 'grid' as 'grid' | 'table',
  },
  effects: {
    *applyCategoryDeepLink({ payload }: any, { put }: any): Generator<any, void, any> {
      yield put({ type: 'setCategoryDeepLinkState', payload });
      yield put({
        type: 'fetchProductsByIngredient',
        analytics: { trigger: 'deep_link' },
        payload: { current: 1, isPagination: false },
      });
    },
    *ingredientAutoComplete(
      { payload, callback }: any,
      { call, put, select }: any,
    ): Generator<any, void, any> {
      const query = typeof payload === 'string' ? payload : payload?.query;
      const updateSuggestions = typeof payload === 'string' || payload?.updateSuggestions !== false;
      let suggestions: any[] = [];

      try {
        if (updateSuggestions) {
          yield put({ type: 'setIngredientSuggestions', payload: [] });
        }

        const response: any = yield call(autoCompleteIngredientName, query);
        suggestions = Array.isArray(response) ? response : [];

        if (updateSuggestions) {
          const currentQuery = yield select(
            (state: any) => state.search.ingredientSearchInput || '',
          );

          if (currentQuery === query) {
            yield put({ type: 'setIngredientSuggestions', payload: suggestions });
          }
        }
      } catch (error) {
        console.error('Failed to fetch suggestions:', error);
      } finally {
        if (callback) callback(suggestions);
      }
    },
    *fetchFilterOptions({ payload }: any, { call, put }: any) {
      try {
        const response: Promise<any> = yield call(fetchFilterOptions, payload);
        yield put({ type: 'setFilterOptions', payload: response });
      } catch (error) {
        console.error('Failed to fetch filter options:', error);
      }
    },
    *clearFilter({ payload, analytics }: any, { put, select }: any) {
      const currentState: Promise<any> = yield select((state: any) => state.search);
      const { filters } = currentState as any;

      const updatedFilters = { ...filters };

      const keysToRemove = Array.isArray(payload) ? payload : [payload];
      keysToRemove.forEach((key) => {
        delete updatedFilters[key];
      });

      yield put({
        type: 'updateState',
        payload: {
          filters: updatedFilters,
        },
      });

      yield put({
        type: 'fetchProductsByIngredient',
        analytics,
        payload: {
          current: 1,
        },
      });
    },
    *fetchProductsByIngredient({ payload, analytics: intent }: any, { call, put, select }: any): Generator<any, void, any> {
      if (payload?.isPagination) {
        yield put({ type: 'changePage', payload: { page: payload.current, pageSize: payload.pageSize }, analytics: intent });
        return;
      }
      const searchState: any = yield select((state: any) => state.search);
      let profileState: any = yield select((state: any) => state.profile);
      const { selectedIngredients, filters, pageSize, interestToggles, interestActions,
        interestSelections, moduleFilters, queryMode, compositeGroups } = searchState;
      const effectiveFilters = payload?.filtersOverride || filters;
      const normalizedCompositeGroups = compositeGroups || createEmptyCompositeGroups();
      if (queryMode === 'composite' && hasInvalidCompositeGroups(normalizedCompositeGroups)) {
        recordFilterApplyBlocked('products_results', 'invalid_composite_query', ['category_of_interest', 'claim_of_interest']);
        message.error('Choose values for every Composite Query filter or remove the empty filter.');
        return;
      }
      const simpleCategoryFilter = moduleFilters?.category_of_interest;
      if (queryMode === 'simple' && simpleCategoryFilter?.mode === 'custom' &&
          !isCompositeGroupConfigured('category_of_interest', simpleCategoryFilter)) {
        recordFilterApplyBlocked('products_results', 'empty_category', ['category_of_interest']);
        message.error('Choose at least one category before searching.');
        return;
      }
      const activeModuleFilters = buildActiveModuleFilters(queryMode, moduleFilters, normalizedCompositeGroups);
      const ingredientIds = selectedIngredients.map((ingredient: any) => ingredient.id);
      const hasModuleFilters = Object.keys(activeModuleFilters).length > 0;

      const invalidRanges = invalidFilterRanges(effectiveFilters);
      if (invalidRanges.length) {
        recordFilterApplyBlocked('products_results', 'invalid_range', invalidRanges);
        message.error('Choose a valid filter range before searching.');
        return;
      }
      // Capture the explicit entry context before portfolio hydration can finish on a later page/session.
      const automatic = intent?.executionKind === 'automatic' || ['initial_load', 'deep_link'].includes(intent?.trigger);
      const action = intent?.action || (intent?.trigger && !automatic
        ? beginAction({ surface: 'products_results', context: intent?.context, trigger: intent.trigger }) : undefined);
      const execution = intent?.execution || (automatic
        ? capturePageExecution(intent?.context || { surface: 'products_results' }) : undefined);
      const token = newId();
      const account = currentAccountScope();
      yield put({ type: 'updateState', payload: { searchRequestToken: token } });
      const normalizedInterestToggles = normalizeInterestToggles(interestToggles);
      const apiPortfolioInterests = normalizePortfolioInterestRequestForApi({
        currentUser: profileState?.userData,
        interestToggles: normalizedInterestToggles,
        interestActions: normalizeInterestActions(interestActions),
        interestSelections: normalizeInterestSelections(interestSelections),
      });
      let productScopeInterests = profileState?.portfolioScopeInterests;
      if (!profileState?.interestListsLoaded?.portfolioScope) {
        productScopeInterests = yield call(fetchPortfolioScopeInterests);
        const latest: any = yield select((state: any) => state.search);
        if (latest.searchRequestToken !== token || (account && !sameAccount(account, currentAccountScope()))) return;
        yield put({ type: 'profile/setPortfolioScopeInterests', payload: productScopeInterests });
      }
      if (!ingredientIds.length && !Object.keys(effectiveFilters).length && !hasModuleFilters && !Object.values(normalizedInterestToggles).some(Boolean) && !hasPortfolioScopeInterests(productScopeInterests)) {
        recordFilterApplyBlocked('products_results', 'empty_criteria');
        return;
      }
      const size = payload?.pageSize || pageSize;
      const params = {
        take: size, skip: 0,
        ingredientIds, module_filters: activeModuleFilters,
        filters: effectiveFilters,
        interestToggles: apiPortfolioInterests.interestToggles,
        interestActions: apiPortfolioInterests.interestActions,
        interestSelections: apiPortfolioInterests.interestSelections,
      };
      let metadata = rememberFilterOptions(searchState.filterMetadata, 'application_ids',
        [...(searchState.filterOptions?.applications || []), ...(productScopeInterests?.applications || [])]);
      metadata = rememberFilterOptions(metadata, 'product_category_ids', searchState.filterOptions?.product_categories || searchState.productCategories);
      metadata = rememberFilterOptions(metadata, 'sub_category_ids', searchState.filterOptions?.sub_categories);
      metadata = rememberFilterOptions(metadata, 'ingredient_ids', selectedIngredients);
      const criteria = filterSnapshot({
        ...params,
        ...moduleFilterValues({ queryMode, moduleFilters, compositeGroups: normalizedCompositeGroups }),
      }, metadata);
      const run = beginSearch({ surface: 'products_results', params, criteria,
        trigger: intent?.trigger, context: intent?.context, action,
        execution, executionKind: intent?.executionKind, workflowType: 'product_discovery' });
      const pageRequest = contextForPageRequest(run, { page: 1, pageSize: size, phase: 'submit', trigger: intent?.trigger });
      if (intent?.trigger === 'apply_filters') recordFiltersApplied({ surface: 'products_results',
        criteria, context: { ...run.search, ...run.action }, reason: intent.applyReason });
      yield put({ type: 'clearProductsData' });
      yield put({ type: 'updateState', payload: { appliedSearch: run, pageRequest, pageSize: size, resultsPage: undefined } });
      try {
        const response: any = yield call(fetchProductsByIngredient, { ...params }, pageRequest.analyticsContext);
        const latest: any = yield select((state: any) => state.search);
        if (!isCurrentSearch(run, latest.appliedSearch) || latest.pageRequest?.id !== pageRequest.id) return;
        const facts = resultCount(response.total, response.countStatus);
        const page = resultsPageForResponse(run, pageRequest, response, facts);
        yield put({ type: 'setProductsForPage', payload: {
          page: 1, data: response.data, total: response.total, countKey: response.countKey,
          countStatus: response.countStatus, ingredientId: response.ingredient_id,
          enterpriseSearch: Boolean((params as any).module_filters && Object.keys((params as any).module_filters).length),
          runId: run.id, pageSize: size, analytics: page,
        } });
        yield put({ type: 'updateState', payload: { resultsPage: page, pageRequest: undefined } });
        yield put({ type: 'resetIngredientSuggestion' });
        if (response.countStatus === 'pending' && response.countKey) yield put({
          type: 'fetchProductsByIngredientCount', payload: { countKey: response.countKey,
            run, execution: executionForResponse(response),
            enterpriseSearch: Boolean((params as any).module_filters && Object.keys((params as any).module_filters).length) },
        });
      } catch (error) {
        const latest: any = yield select((state: any) => state.search);
        if (!isCurrentSearch(run, latest.appliedSearch) || latest.pageRequest?.id !== pageRequest.id) return;
        yield put({ type: 'updateState', payload: { pageRequest: undefined } });
        message.error('Failed to fetch products');
      }
    },
    *changePage({ payload, analytics: intent }: any, { call, put, select }: any): Generator<any, void, any> {
      const state: any = yield select((state: any) => state.search);
      const run = state.appliedSearch;
      const page = payload.page;
      const size = payload.pageSize || state.pageSize;
      const sizeChanged = size !== state.pageSize;
      if (!Number.isSafeInteger(page) || page < 1 || !Number.isSafeInteger(size) || size < 1) return;
      if ((page === state.currentPage && !sizeChanged && !state.pageRequest && intent?.trigger !== 'retry') ||
          (state.pageRequest?.page === page && state.pageRequest?.pageSize === size)) return;
      if (!run) {
        // An already populated pre-migration cache stays usable; no attributed view is fabricated.
        if (state.productsData.pages[page]) yield put({ type: 'updateState', payload: { currentPage: page, resultsPage: undefined } });
        return;
      }
      if (!isCurrentSearch(run, run)) return;
      const pageRequest = contextForPageRequest(run, { page, pageSize: size,
        previousPage: state.currentPage, phase: 'page', trigger: intent?.trigger,
        action: intent?.action, execution: intent?.execution,
        navigationMethod: intent?.navigationMethod || navigationMethod(state.currentPage, page, sizeChanged) });
      const cached = state.productsData.pages[page];
      if (!sizeChanged && cached?.runId === run.id && cached.pageSize === size && intent?.trigger !== 'retry') {
        const view = cached.analytics ? resultsPageFromCache({ ...cached.analytics,
          ...resultCount(state.productsData.total, state.productsData.countStatus) }, pageRequest) : undefined;
        yield put({ type: 'updateState', payload: { currentPage: page, resultsPage: view, pageRequest: undefined } });
        return;
      }
      yield put({ type: 'updateState', payload: { pageRequest } });
      try {
        const params = { ...run.params, take: size, skip: (page - 1) * size, countKey: state.productsData.countKey };
        const response: any = yield call(fetchProductsByIngredient, params, pageRequest.analyticsContext);
        const latest: any = yield select((state: any) => state.search);
        if (!isCurrentSearch(run, latest.appliedSearch) || latest.pageRequest?.id !== pageRequest.id) return;
        if (sizeChanged) yield put({ type: 'updateState', payload: { productsData: { ...latest.productsData, pages: {} }, pageSize: size } });
        const view = resultsPageForResponse(run, pageRequest, response, resultCount(response.total, response.countStatus));
        yield put({ type: 'setProductsForPage', payload: { page, data: response.data, total: response.total,
          countKey: response.countKey, countStatus: response.countStatus, runId: run.id, pageSize: size, analytics: view } });
        yield put({ type: 'updateState', payload: { resultsPage: view, pageRequest: undefined } });
        if (response.countStatus === 'pending' && response.countKey &&
            (latest.productsData.countKey !== response.countKey || latest.productsData.countStatus !== 'pending')) {
          yield put({ type: 'fetchProductsByIngredientCount', payload: { countKey: response.countKey,
            run, execution: executionForResponse(response), enterpriseSearch: state.productsData.enterpriseSearch } });
        }
      } catch (error) {
        const latest: any = yield select((state: any) => state.search);
        if (!isCurrentSearch(run, latest.appliedSearch) || latest.pageRequest?.id !== pageRequest.id) return;
        yield put({ type: 'updateState', payload: { pageRequest: undefined } });
        message.error('Failed to fetch products for page');
      }
    },
    *fetchProductsByIngredientCount({ payload }: any, { call, put, select }: any): Generator<any, void, any> {
      const { countKey, run, execution } = payload || {};
      if (!countKey) return;
      try {
        for (let attempt = 0; attempt < PRODUCT_COUNT_MAX_POLLS; attempt += 1) {
          const before: any = yield select((state: any) => state.search);
          if (before.productsData.countKey !== countKey || (run && !isCurrentSearch(run, before.appliedSearch))) return;
          const response: any = yield call(fetchProductsByIngredientCount,
            { countKey, enterpriseSearch: payload.enterpriseSearch === true }, contextForDeferredCount(execution, run));
          const current: any = yield select((state: any) => state.search);
          if (current.productsData.countKey !== countKey || (run && !isCurrentSearch(run, current.appliedSearch))) return;
          if (response.countStatus === 'pending') {
            yield call(waitForProductCount, PRODUCT_COUNT_POLL_INTERVAL_MS);
            continue;
          }
          const count = resultCount(response.total, response.countStatus);
          yield put({ type: 'setProductSearchCount', payload: { countKey, total: count.totalCount, countStatus: count.countStatus } });
          return;
        }
        throw new Error('Product count did not finish before the polling deadline');
      } catch (error) {
        const current: any = yield select((state: any) => state.search);
        if (current.productsData.countKey !== countKey || (run && !isCurrentSearch(run, current.appliedSearch))) return;
        yield put({ type: 'setProductSearchCount', payload: { countKey, total: null, countStatus: 'unavailable' } });
      }
    },
    *fetchProduct({ payload, callback }: any, { call, put, select }: any) {
      const account = currentAccountScope();
      const searchState: Promise<any> = yield select((state: any) => state.search);
      const { productsData } = searchState as any;

      const isViewed = Object.values(productsData.pages).some((pageData: any) => {
        return pageData.data.some((product: any) => product.id === payload.id && product.viewed);
      });

      if (isViewed) {
        if (callback) callback();
        return;
      }

      const product: Promise<any> = yield call(fetchProduct, payload);
      if (account && !sameAccount(account, currentAccountScope())) return;
      yield put({ type: 'mergeProductDetails', payload: product });

      if (callback) callback();
    },
    *fetchProductLists(_: any, { call, put }: any) {
      const response: Promise<any> = yield call(fetchProductLists);
      yield put({
        type: 'setProductLists',
        payload: response,
      });
    },
    *createProductList({ payload, callback }: any, { call, put, select }: any) {
      try {
        const response: Promise<any> = yield call(createProductList, payload);
        const existingProductLists: Promise<any> = yield select(
          (state: any) => state.search.productLists,
        );

        let newProductLists = [
          ...(existingProductLists as any),
          {
            id: (response as any).listId,
            name: payload.name,
            created_at: new Date().toISOString(),
          },
        ];

        newProductLists = newProductLists.sort(
          (a: any, b: any) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
        );

        yield put({
          type: 'setProductLists',
          payload: newProductLists,
        });
        if (callback) callback();
      } catch (error: any) {
        if (callback) callback();
        message.error(error.response.data.message);
      }
    },
    *addProductsToList({ payload, callback }: any, { call, select, put }: any) {
      const { listId, productIds: requestedProductIds } = payload;
      const { selectedRows } = yield select((state: any) => state.search);
      const productIds = Array.from(
        new Set<number>(
          (Array.isArray(requestedProductIds) ? requestedProductIds : selectedRows)
            .map(Number)
            .filter((id: number) => Number.isInteger(id) && id > 0),
        ),
      );

      const response: Promise<any> = yield call(addProductsToList, {
        listId,
        productIds,
      });

      if ((response as any)?.success && (response as any)?.products) {
        let searchState: Promise<any> = yield select((state: any) => state.search);
        const { productsData } = searchState as any;

        const updatedPages = Object.entries(productsData.pages).reduce(
          (acc: any, [pageNum, pageData]: any) => {
            const updatedData = pageData.data.map((product: any) => {
              const updatedProduct = (response as any).products.find(
                (p: any) => p.id === product.id,
              );
              if (updatedProduct) {
                return {
                  ...updatedProduct,
                  matched_ingredient_ids:
                    updatedProduct.matched_ingredient_ids ?? product.matched_ingredient_ids,
                  matched_client_products:
                    updatedProduct.matched_client_products ?? product.matched_client_products,
                  matched_client_categories:
                    updatedProduct.matched_client_categories ?? product.matched_client_categories,
                  matched_category_groups:
                    updatedProduct.matched_category_groups ?? product.matched_category_groups,
                  countries_of_sale:
                    updatedProduct.country_of_sale || updatedProduct.countries_of_sale,
                  marketed_by: updatedProduct.marketer?.name,
                  manufactured_by:
                    updatedProduct.manufacturer?.name || updatedProduct.manufacturer_name,
                  viewed: true,
                };
              }
              return product;
            });

            acc[pageNum] = {
              ...pageData,
              data: updatedData,
            };
            return acc;
          },
          {},
        );

        yield put({
          type: 'updateState',
          payload: {
            productsData: {
              ...productsData,
              pages: updatedPages,
            },
          },
        });
      }

      if (callback) {
        callback(response);
      }
    },
    *fetchProductCategories(_: any, { call, put }: any) {
      const response: Promise<any> = yield call(fetchProductCategories);

      yield put({
        type: 'setProductCategories',
        payload: response,
      });
    },
  },
  reducers: {
    mergeProductDetails(state: any, { payload: product }: any) {
      const pages = Object.fromEntries(Object.entries(state.productsData.pages).map(([page, entry]: any) => [page, {
        ...entry,
        data: entry.data.map((row: any) => row.id !== product.id ? row : {
          ...row, ...product,
          matched_ingredient_ids: product.matched_ingredient_ids ?? row.matched_ingredient_ids,
          matched_client_products: product.matched_client_products ?? row.matched_client_products,
          matched_client_categories: product.matched_client_categories ?? row.matched_client_categories,
          matched_category_groups: product.matched_category_groups ?? row.matched_category_groups,
          countries_of_sale: product.country_of_sale || product.countries_of_sale,
          marketed_by: product.marketer?.name,
          manufactured_by: product.manufacturer?.name,
          viewed: true,
        }),
      }]));
      return { ...state, productsData: { ...state.productsData, pages } };
    },
    setCategoryDeepLinkState(state: any, { payload }: any) {
      return applyCategoryDeepLinkState(state, payload);
    },
    updateState(state: any, { payload }: any) {
      return {
        ...state,
        ...payload,
      };
    },
    setProductsForPage(state: any, { payload }: any) {
      const { page, data, total, countKey, countStatus } = payload;
      const nextTotal = total !== undefined ? total : state.productsData.total;
      return { ...state, currentPage: page, productsData: { ...state.productsData,
        pages: { ...state.productsData.pages, [page]: { ...state.productsData.pages[page],
          data, total: nextTotal,
          ...(payload.runId ? { runId: payload.runId, pageSize: payload.pageSize, analytics: payload.analytics } : {}),
        } }, total: nextTotal,
        countKey: countKey !== undefined ? countKey : state.productsData.countKey,
        countStatus: countStatus || state.productsData.countStatus,
        ...(payload.enterpriseSearch !== undefined ? { enterpriseSearch: payload.enterpriseSearch } : {}),
        ...(payload.ingredientId !== undefined ? { ingredientId: payload.ingredientId } : {}),
      } };
    },
    setProductSearchCount(state: any, { payload }: any) {
      if (state.productsData.countKey !== payload.countKey) {
        return state;
      }

      const pages = Object.entries(state.productsData.pages).reduce(
        (acc: any, [page, pageData]: any) => {
          acc[page] = {
            ...pageData,
            total: payload.total,
          };
          return acc;
        },
        {},
      );

      return {
        ...state,
        resultsPage: state.resultsPage ? { ...state.resultsPage, ...resultCount(payload.total, payload.countStatus) } : undefined,
        productsData: {
          ...state.productsData,
          pages,
          total: payload.total,
          countStatus: payload.countStatus,
        },
      };
    },
    setProductLists(state: any, { payload }: any) {
      return {
        ...state,
        productLists: payload,
      };
    },
    addToSelectedRows(state: any, { payload }: any) {
      if (!state.selectedRows.includes(payload)) {
        return {
          ...state,
          selectedRows: [...state.selectedRows, payload],
        };
      }
      return state;
    },
    removeFromSelectedRows(state: any, { payload }: any) {
      return {
        ...state,
        selectedRows: state.selectedRows.filter((id: any) => id !== payload),
      };
    },
    addAllProductsToSelectedRows(state: any) {
      const { currentPage, productsData } = state;
      const currentPageData = productsData.pages[currentPage]?.data || [];
      const allIds = currentPageData.map((item: any) => item.id);
      const newSelectedRows = Array.from(new Set([...state.selectedRows, ...allIds]));
      return {
        ...state,
        selectedRows: newSelectedRows,
      };
    },
    clearSelectedRows(state: any) {
      return {
        ...state,
        selectedRows: [],
      };
    },
    setIngredientSuggestions(state: any, { payload }: any) {
      return {
        ...state,
        ingredientSuggestions: payload,
      };
    },
    resetIngredientSuggestion(state: any) {
      return {
        ...state,
        ingredientSuggestions: [],
      };
    },
    addSelectedIngredient(state: any, { payload }: any) {
      // payload: { id: number, name: string }
      const alreadySelected = state.selectedIngredients.some((i: any) => i.id === payload.id);
      if (alreadySelected) return state;
      return {
        ...state,
        selectedIngredients: [...state.selectedIngredients, payload],
      };
    },
    removeSelectedIngredient(state: any, { payload }: any) {
      // payload: ingredient id
      return {
        ...state,
        selectedIngredients: state.selectedIngredients.filter((i: any) => i.id !== payload),
      };
    },
    setFilterOptions(state: any, { payload }: any) {
      return {
        ...state,
        filterOptions: payload,
        filterOptionsLoaded: true,
      };
    },
    updateSearchValue(state: any, { payload }: any) {
      return {
        ...state,
        ingredientSearchInput: payload,
      };
    },
    updateFilters(state: any, { payload }: any) {
      const cleanedFilters = Object.entries(payload).reduce((acc: any, [key, value]) => {
        if (
          value !== undefined &&
          value !== null &&
          value !== '' &&
          !(Array.isArray(value) && value.length === 0)
        ) {
          acc[key] = value;
        }
        return acc;
      }, {});

      return {
        ...state,
        filters: cleanedFilters,
      };
    },
    updateModuleFilter(state: any, { payload }: any) {
      const key = String(payload?.key || '').trim();
      if (!key) return state;
      const moduleFilters = { ...(state.moduleFilters || {}) };
      if (payload.value === undefined || payload.value === null) {
        delete moduleFilters[key];
      } else {
        moduleFilters[key] = payload.value;
      }
      if (payload.preserveResults === true) {
        return {
          ...state,
          moduleFilters,
        };
      }
      return {
        ...state,
        moduleFilters,
        productsData: {
          pages: {},
          total: null,
          countKey: null,
          countStatus: 'idle',
          enterpriseSearch: Object.keys(moduleFilters).length > 0,
        },
        currentPage: 1,
      };
    },
    setQueryMode(state: any, { payload }: any) {
      const requestedMode = typeof payload === 'object' ? payload?.mode : payload;
      const queryMode = requestedMode === 'composite' ? 'composite' : 'simple';
      if (queryMode !== 'composite') {
        return { ...state, queryMode };
      }

      const requestedTypes = Array.isArray(payload?.enabledFilterTypes)
        ? payload.enabledFilterTypes
        : ['category_of_interest', 'claim_of_interest'];
      const enabledFilterTypes = requestedTypes.filter(
        (type: string) => type === 'category_of_interest' || type === 'claim_of_interest',
      );

      const compositeGroups = {
        ...createEmptyCompositeGroups(),
        ...(state.compositeGroups || {}),
      };
      let sequence = Number(state.compositeGroupSequence) || 0;
      const createPrimaryGroup = (type: string, value: any) => {
        sequence += 1;
        return { id: `${type}-${sequence}`, value };
      };

      if (
        enabledFilterTypes.includes('category_of_interest') &&
        compositeGroups.category_of_interest.length === 0
      ) {
        compositeGroups.category_of_interest = [
          createPrimaryGroup('category_of_interest', state.moduleFilters?.category_of_interest),
        ];
      }
      if (
        enabledFilterTypes.includes('claim_of_interest') &&
        compositeGroups.claim_of_interest.length === 0
      ) {
        compositeGroups.claim_of_interest = [
          createPrimaryGroup('claim_of_interest', state.moduleFilters?.claim_of_interest),
        ];
      }
      if (!enabledFilterTypes.includes('category_of_interest')) {
        compositeGroups.category_of_interest = [];
      }
      if (!enabledFilterTypes.includes('claim_of_interest')) {
        compositeGroups.claim_of_interest = [];
      }

      return {
        ...state,
        queryMode,
        compositeGroups,
        compositeGroupSequence: sequence,
      };
    },
    updateCompositeGroup(state: any, { payload }: any) {
      const type = payload?.type;
      if (type !== 'category_of_interest' && type !== 'claim_of_interest') return state;
      const compositeGroups = {
        ...createEmptyCompositeGroups(),
        ...(state.compositeGroups || {}),
      };
      compositeGroups[type] = compositeGroups[type].map((group: any) =>
        group.id === payload.id ? { ...group, value: payload.value } : group,
      );
      return { ...state, compositeGroups };
    },
    addCompositeGroup(state: any, { payload }: any) {
      const type = payload?.type;
      if (type !== 'category_of_interest' && type !== 'claim_of_interest') return state;
      const compositeGroups = {
        ...createEmptyCompositeGroups(),
        ...(state.compositeGroups || {}),
      };
      if (compositeGroups[type].length >= MAX_COMPOSITE_GROUPS_PER_TYPE) return state;
      const sequence = (Number(state.compositeGroupSequence) || 0) + 1;
      compositeGroups[type] = [
        ...compositeGroups[type],
        { id: `${type}-${sequence}`, value: undefined },
      ];
      return {
        ...state,
        compositeGroups,
        compositeGroupSequence: sequence,
      };
    },
    removeCompositeGroup(state: any, { payload }: any) {
      const type = payload?.type;
      if (type !== 'category_of_interest' && type !== 'claim_of_interest') return state;
      const compositeGroups = {
        ...createEmptyCompositeGroups(),
        ...(state.compositeGroups || {}),
      };
      const index = compositeGroups[type].findIndex((group: any) => group.id === payload.id);
      if (index <= 0) return state;
      compositeGroups[type] = compositeGroups[type].filter((group: any) => group.id !== payload.id);
      return { ...state, compositeGroups };
    },
    updateInterestToggle(state: any, { payload }: any) {
      const key = payload?.key;
      if (!INTEREST_KEYS.includes(key)) {
        return state;
      }

      const interestSelections = normalizeInterestSelections(state.interestSelections);
      const currentInterestActions = normalizeInterestActions(state.interestActions) as Record<
        string,
        string
      >;
      delete interestSelections[key];

      return {
        ...state,
        interestToggles: {
          ...normalizeInterestToggles(state.interestToggles),
          [key]: payload.value === true,
        },
        interestActions: {
          ...currentInterestActions,
          [key]:
            payload.action === 'include' || payload.action === 'exclude'
              ? payload.action
              : currentInterestActions[key],
        },
        interestSelections,
      };
    },
    updateInterestSelection(state: any, { payload }: any) {
      const key = payload?.key;
      if (!INTEREST_KEYS.includes(key)) {
        return state;
      }

      return {
        ...state,
        interestToggles: {
          ...normalizeInterestToggles(state.interestToggles),
          [key]: true,
        },
        interestActions: {
          ...normalizeInterestActions(state.interestActions),
          [key]: 'include',
        },
        interestSelections: {
          ...normalizeInterestSelections(state.interestSelections),
          [key]: Array.from(new Set<number>((payload.selectedIds || []).map(Number))).filter(
            (id: number) => Number.isInteger(id) && id > 0,
          ),
        },
      };
    },
    resetFilters(state: any) {
      return {
        ...state,
        appliedSearch: undefined, pageRequest: undefined, resultsPage: undefined, searchRequestToken: undefined, filterMetadata: {},
        filters: {},
        interestToggles: { ...DEFAULT_INTEREST_TOGGLES },
        interestActions: { ...DEFAULT_INTEREST_ACTIONS },
        interestSelections: {},
        moduleFilters: {},
        queryMode: 'simple',
        compositeGroups: createEmptyCompositeGroups(),
        compositeGroupSequence: 0,
        ingredientSearchInput: '',
        selectedIngredients: [],
        productsData: {
          pages: {},
          total: null,
          countKey: null,
          countStatus: 'idle',
        },
        currentPage: 1,
      };
    },
    toggleFilterDrawer(state: any) {
      return {
        ...state,
        filterDrawerVisible: !state.filterDrawerVisible,
      };
    },
    toggleListModal(state: any) {
      return {
        ...state,
        listModalVisible: !state.listModalVisible,
      };
    },
    setProductCategories(state: any, { payload }: any) {
      return {
        ...state,
        productCategories: payload,
        productCategoriesLoaded: true,
      };
    },
    setViewMode(state: any, { payload }: any) {
      return {
        ...state,
        viewMode: payload,
      };
    },
    resetAll(state: any) {
      return {
        ...state,
        appliedSearch: undefined, pageRequest: undefined, resultsPage: undefined, searchRequestToken: undefined, filterMetadata: {},
        ingredientSearchInput: '',
        selectedIngredients: [],
        filters: {},
        interestToggles: { ...DEFAULT_INTEREST_TOGGLES },
        interestActions: { ...DEFAULT_INTEREST_ACTIONS },
        interestSelections: {},
        moduleFilters: {},
        queryMode: 'simple',
        compositeGroups: createEmptyCompositeGroups(),
        compositeGroupSequence: 0,
        productsData: {
          pages: {},
          total: null,
          countKey: null,
          countStatus: 'idle',
        },
        selectedRows: [],
        ingredientSuggestions: [],
        currentPage: 1,
      };
    },
    clearProductsData(state: any) {
      return {
        ...state,
        appliedSearch: undefined, pageRequest: undefined, resultsPage: undefined, searchRequestToken: undefined,
        productsData: {
          pages: {},
          total: null,
          countKey: null,
          countStatus: 'idle',
        },
        currentPage: 1,
      };
    },
  },
};

export default Model;
